import React, { useEffect, useState } from 'react';

interface SubsystemStatus {
  meshBus: string;
  websocketRelay: string;
  webrtcSignaling: string;
  ragEngine: string;
}

interface HealthData {
  status: string;
  version: string;
  nodeId: string;
  subsystems: SubsystemStatus;
}

const statusColor = (s: string) => {
  if (s === 'ONLINE' || s === 'READY' || s === 'ACTIVE') return '#34d399';
  if (s === 'DEGRADED' || s === 'WARN') return '#f59e0b';
  return '#ef4444';
};

const dot = (s: string) => (
  <span style={{ color: statusColor(s), fontWeight: 700 }}>●&nbsp;{s}</span>
);

export const HealthDashboard: React.FC = () => {
  const [health, setHealth] = useState<HealthData | null>(null);
  const [wsCount, setWsCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [peers, setPeers] = useState<Array<{ nodeId: string; addressUri: string; status: string }>>([]);

  const fetchHealth = async () => {
    try {
      const [hRes, pRes] = await Promise.all([
        fetch('http://localhost:8080/api/health'),
        fetch('http://localhost:8080/api/peers'),
      ]);
      const hData: HealthData = await hRes.json();
      const pData = await pRes.json();
      setHealth(hData);
      setWsCount(pData.count ?? null);
      setPeers(pData.peers ?? []);
      setError(null);
      setLastUpdated(new Date());
    } catch (e: any) {
      setError('Cannot reach Mesh Gateway on port 8080. Server may be offline.');
    }
  };

  useEffect(() => {
    fetchHealth();
    const timer = setInterval(fetchHealth, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      id="health-dashboard"
      style={{
        minHeight: '100vh',
        backgroundColor: '#0b0f19',
        color: '#f1f5f9',
        fontFamily: "'Inter', 'SF Pro', system-ui, sans-serif",
        padding: '32px',
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 700, margin: 0, color: '#f8fafc' }}>
            🛡️ Sovereign Mesh — Health Dashboard
          </h1>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
            {lastUpdated ? `Last updated: ${lastUpdated.toLocaleTimeString()}` : 'Loading...'}
          </div>
        </div>
        <button
          onClick={fetchHealth}
          style={{
            background: 'rgba(59, 130, 246, 0.15)',
            border: '1px solid rgba(59, 130, 246, 0.4)',
            borderRadius: '8px',
            color: '#60a5fa',
            cursor: 'pointer',
            padding: '8px 16px',
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          ↻ Refresh
        </button>
      </div>

      {error && (
        <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: '10px', padding: '16px', marginBottom: '24px', color: '#fca5a5', fontSize: '13px' }}>
          ⚠️ {error}
        </div>
      )}

      {health && (
        <>
          {/* Primary Status Card */}
          <div style={{ backgroundColor: '#0f1929', border: '1px solid #1e293b', borderRadius: '12px', padding: '24px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div>
                <div style={{ fontSize: '13px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>
                  Gateway Status
                </div>
                <div style={{ fontSize: '28px', fontWeight: 800, color: statusColor(health.status) }}>
                  {health.status}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Version</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#94a3b8' }}>{health.version}</div>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>Node ID</div>
                <div style={{ fontSize: '11px', fontFamily: 'monospace', color: '#60a5fa' }}>{health.nodeId}</div>
              </div>
            </div>

            {/* Subsystems Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {Object.entries(health.subsystems).map(([key, value]) => (
                <div
                  key={key}
                  style={{
                    backgroundColor: '#151d2a',
                    border: '1px solid #1e293b',
                    borderRadius: '8px',
                    padding: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600 }}>
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </div>
                  <div style={{ fontSize: '12px' }}>{dot(value as string)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Peers Card */}
          <div style={{ backgroundColor: '#0f1929', border: '1px solid #1e293b', borderRadius: '12px', padding: '24px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '14px', fontWeight: 700, margin: 0 }}>
                Connected Peers
              </h2>
              <span style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa', fontSize: '11px', fontWeight: 700, padding: '2px 10px', borderRadius: '12px' }}>
                {wsCount ?? '?'} active
              </span>
            </div>
            {peers.length === 0 ? (
              <div style={{ color: '#64748b', fontSize: '12px', fontStyle: 'italic' }}>No peer data available</div>
            ) : (
              peers.map((p) => (
                <div
                  key={p.nodeId}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #1e293b' }}
                >
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600 }}>{p.nodeId}</div>
                    <div style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>{p.addressUri}</div>
                  </div>
                  <div style={{ fontSize: '12px' }}>{dot(p.status)}</div>
                </div>
              ))
            )}
          </div>

          {/* API Links */}
          <div style={{ backgroundColor: '#0f1929', border: '1px solid #1e293b', borderRadius: '12px', padding: '24px' }}>
            <h2 style={{ fontSize: '14px', fontWeight: 700, margin: '0 0 14px' }}>Mesh API Endpoints</h2>
            {['/api/health', '/api/peers', '/api/broadcasts'].map((ep) => (
              <div
                key={ep}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #1e293b' }}
              >
                <code style={{ fontSize: '12px', color: '#60a5fa' }}>http://localhost:8080{ep}</code>
                <a
                  href={`http://localhost:8080${ep}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: '11px', color: '#94a3b8', textDecoration: 'none' }}
                >
                  Open ↗
                </a>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
